import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound } from 'lucide-react';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { verifyTotp } from '../api/client';
import type { Stage } from '../api/schemas';
import { Button } from '../ui/elements';
import { ANMELDE_INPUT_CLASS, AnmeldeFeld } from './felder';
import { FieldError } from './LoginPage';

/**
 * Die Eingabe des Einmalkennworts.
 *
 * **Leerzeichen werden entfernt, bevor die Anfrage hinausgeht.** Viele Apps
 * zeigen `123 456`, und wer es abschreibt, tippt das Leerzeichen mit. Eine
 * Anmeldung daran scheitern zu lassen wäre eine Schikane ohne
 * Sicherheitsgewinn — der Server entfernt es ebenfalls, die Bereinigung hier
 * erspart bloss die Fehlermeldung.
 *
 * **Das Schema prüft die bereinigte Form, gibt aber die rohe zurück.** Eine
 * `.transform()` im Schema wäre kürzer und zöge einen zweiten Werttyp durch
 * das Formular; für eine Zeile `replace` ist das kein guter Handel.
 *
 * **Das Feld ist für Passwortverwalter erkennbar** (`A-sitzungssperre`,
 * Fall `E-1129`): `autocomplete="one-time-code"`, `inputmode="numeric"`,
 * `pattern="[0-9]*"` und — der Punkt, an dem es vorher scheiterte — `name`
 * und `id` mit dem Wert `totp`. Dieselben vier Merkmale trägt das Feld des
 * Sperrbildschirms (`auth/Sperrbildschirm.tsx`); es sind die zwei Stellen
 * dieser Anwendung, an denen ein Einmalkennwort eingegeben wird.
 *
 * **Dieses Formular steht ausschliesslich auf der Anmeldebühne** — direkt in
 * `LoginPage` oder in `TotpEnrollForm`, das ebenfalls nur dort erscheint.
 * Deshalb trägt es die Feldform der Anmeldung (44 px, `AnmeldeFeld`) ohne
 * Schalter; `PasswordChangeForm` braucht einen, weil es zusätzlich in einer
 * Tafel der angemeldeten Anwendung steht.
 */

/** Entfernt jeden Zwischenraum. Eine Stelle, zweimal benutzt. */
function normalize(value: string): string {
  return value.replace(/\s+/g, '');
}

/**
 * Sechs Ziffern — gemessen an der bereinigten Eingabe.
 *
 * **Das Feld heisst `totp` und nicht mehr `token`** (`A-sitzungssperre`,
 * Meldung des Auftraggebers vom 2026-09-10: «Das 2FA-Feld nach dem Login ist
 * nicht totp, sondern token — Bitwarden erkennt es nicht für Autofill»).
 * `useForm().register('totp')` setzt daraus das `name`-Attribut des `<input>`,
 * und genau daran erkennen die verbreiteten Passwortverwalter ein
 * Einmalkennwortfeld — `autocomplete="one-time-code"` allein genügt ihnen
 * nicht. Der **Rumpf** der Anfrage heisst unverändert `token`: Das ist der
 * veröffentlichte Vertrag von `POST /auth/totp/verify`, und der wird nicht
 * umbenannt, weil ein Attribut im Browser anders heissen soll.
 */
const schema = z.object({
  totp: z.string().refine((value) => /^\d{6}$/.test(normalize(value)), {
    message: 'Das Einmalkennwort besteht aus sechs Ziffern.',
  }),
});

/** Die Eingabe des Formulars. */
type Values = z.infer<typeof schema>;

export function TotpForm(props: {
  readonly onStage: (stage: Stage) => void;
  readonly onFailure: (error: unknown) => void;
  readonly label?: string;
}): ReactNode {
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { totp: '' },
  });

  const submit = async (values: Values): Promise<void> => {
    try {
      props.onStage(await verifyTotp(normalize(values.totp)));
    } catch (error) {
      props.onFailure(error);
      form.reset({ totp: '' });
    }
  };

  return (
    <form
      className="flex flex-col gap-4"
      noValidate
      onSubmit={(event) => {
        void form.handleSubmit(submit)(event);
      }}
    >
      {/*
        **Die Beschriftungen bleiben wörtlich.** `e2e/harness.ts` greift
        «Einmalkennwort» exakt, `e2e/first-login.spec.ts` «Einmalkennwort aus
        der App» — beides der zugängliche Name des Feldes.
      */}
      <div>
        {/*
          **Die Beschriftung bleibt «Einmalkennwort», der erklärende Satz steht
          als Hinweis DARUNTER.** Das Konzept des Sperrbildschirms formuliert
          ihn als Beschriftung («6-stelliger Code aus deiner
          Authenticator-App»); hier ginge das nicht, ohne den zugänglichen
          Namen des Feldes zu ändern — und genau darüber finden es
          `e2e/harness.ts` («Einmalkennwort», exakt) und
          `e2e/first-login.spec.ts` («Einmalkennwort aus der App»).
          `AnmeldeFeld.hinweis` steht ausdrücklich AUSSERHALB der Beschriftung
          (siehe dort) und ist der Ort für genau diesen Fall. Der Anwender
          liest denselben Satz, die Prüfungen finden dasselbe Feld.
        */}
        <AnmeldeFeld
          label={props.label ?? 'Einmalkennwort'}
          sinnbild={<KeyRound />}
          hinweis="6-stelliger Code aus deiner Authenticator-App"
          feldId="totp"
        >
          <input
            type="text"
            inputMode="numeric"
            // `pattern` bringt auf dem Telefon den Ziffernblock auch dort, wo
            // `inputMode` nicht greift, und ist für Passwortverwalter das
            // dritte Merkmal neben `autocomplete` und `name`.
            pattern="[0-9]*"
            autoComplete="one-time-code"
            // **Sieben und nicht sechs**, und das ist Absicht: Viele Apps
            // zeigen «123 456», und wer das abschreibt oder einfügt, bringt
            // das Leerzeichen mit — `normalize()` entfernt es, aber ein
            // `maxLength` von sechs hätte die Eingabe schon vorher auf
            // «123 45» beschnitten. Die Bereinigung oben wäre damit tot.
            maxLength={7}
            autoFocus
            placeholder="000000"
            className={`${ANMELDE_INPUT_CLASS} zahl tracking-widest`}
            {...form.register('totp')}
          />
        </AnmeldeFeld>
        <FieldError message={form.formState.errors.totp?.message} />
      </div>

      <Button
        type="submit"
        disabled={form.formState.isSubmitting}
        className="mt-1.5 h-11 w-full rounded-[10px] text-sm font-semibold"
      >
        Bestätigen
      </Button>
    </form>
  );
}
